import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Storefront, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/i18n/I18nContext";
import PasswordChecklist, { isPasswordStrong } from "@/components/PasswordChecklist";

const BG = "https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", business_type: "salon", currency: "CRC" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register({ ...form, lang });
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-[#FDFBF9]">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Salon" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1E2B25]/55" />
        <div className="relative h-full flex flex-col justify-between p-12 text-[#FDFBF9]">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center"><Storefront size={20} weight="duotone" /></div>
            <span className="font-display font-extrabold text-xl tracking-tight">BellaOS</span>
          </Link>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-light leading-tight tracking-tight">{t.auth.regHeroTitle}</h1>
            <p className="mt-5 text-white/75 text-base max-w-md leading-relaxed">{t.auth.regHeroSub}</p>
          </div>
          <div className="text-xs tracking-[0.2em] uppercase text-white/50">{t.auth.regHeroTags}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto fade-up">
        <div className="w-full max-w-md py-8">
          <div className="flex items-center justify-between mb-8">
            <div><p className="label-caps">{t.auth.getStarted}</p></div>
            <LanguageToggle />
          </div>
          <h2 className="font-heading text-3xl font-medium tracking-tight mb-8">{t.auth.createTitle}</h2>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-caps block mb-2">{t.auth.yourName}</label>
              <input data-testid="register-name" value={form.name} onChange={set("name")} required
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition" placeholder="Isabella Moreau" />
            </div>
            <div>
              <label className="label-caps block mb-2">{t.auth.businessName}</label>
              <input data-testid="register-business" value={form.business_name} onChange={set("business_name")} required
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition" placeholder="Bella Studio" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">{t.auth.type}</label>
                <select data-testid="register-type" value={form.business_type} onChange={set("business_type")}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] transition">
                  <option value="salon">{lang === "en" ? "Beauty Salon" : "Salón de Belleza"}</option>
                  <option value="barbershop">{lang === "en" ? "Barbershop" : "Barbería"}</option>
                  <option value="spa">Spa</option>
                  <option value="nails">{lang === "en" ? "Nail Studio" : "Estudio de Uñas"}</option>
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">{t.auth.currency}</label>
                <select data-testid="register-currency" value={form.currency} onChange={set("currency")}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] transition">
                  <option value="CRC">CRC ₡</option>
                  <option value="USD">USD $</option>
                  <option value="EUR">EUR €</option>
                  <option value="MXN">MXN $</option>
                  <option value="BRL">BRL R$</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-caps block mb-2">{t.auth.email}</label>
              <input data-testid="register-email" type="email" value={form.email} onChange={set("email")} required
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition" placeholder="you@studio.com" />
            </div>
            <div>
              <label className="label-caps block mb-2">{t.auth.password}</label>
              <input data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={10}
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition" placeholder="••••••••••" />
              <PasswordChecklist password={form.password} />
            </div>

            {error && <div data-testid="register-error" className="text-sm text-[#B86B5A] bg-[#B86B5A]/8 rounded-lg px-4 py-2.5">{error}</div>}

            <button data-testid="register-submit" type="submit" disabled={loading || !isPasswordStrong(form.password)}
              className="w-full py-3 rounded-lg bg-[#2C3E35] text-white font-medium hover:bg-[#1E2B25] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? t.auth.creating : t.auth.create} {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#6B6A67]">
            {t.auth.haveAccount}{" "}
            <Link to="/login" data-testid="goto-login" className="text-[#2C3E35] font-semibold hover:text-[#B86B5A] transition-colors">{t.auth.signin}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
